import { EVMAnalyticsEvent } from "./events"
import { EVMAssetSurface, EVMTransformType } from "./propertyEnums"
import type { IVMFunnelDefinition, IVMFunnelDefinitionErased } from "./types"

/**
 * Binds the step tuple so `filters` and `breakdowns` are checked against the properties
 * common to EVERY step. The `const` type parameter preserves the literal tuple — without
 * it the steps widen to `EVMAnalyticsEvent[]` and the whole check evaporates.
 */
const defineFunnel = <const TSteps extends readonly EVMAnalyticsEvent[]>(
  definition: IVMFunnelDefinition<TSteps>,
): IVMFunnelDefinition<TSteps> => definition

/**
 * Canonical funnels — the named measurements the product is judged by.
 *
 * NOT YET WIRED TO DASHBOARDS: the intent is for tiles to carry `analyticsRef.funnelKey`
 * so the audit can prove a tile's PostHog query still matches the funnel it claims to
 * render. No committed tile carries that key today and no check reads it, so a tile and
 * its funnel can still drift apart silently. Until then the audit only verifies that
 * dashboards do not query unknown or sunset EVENTS.
 *
 * Adding a breakdown that some step does not carry is a compile error — that is the point.
 * If the compiler rejects a breakdown you want, the fix is to declare the property on the
 * missing step (see IVMCrossFunnelProps), not to weaken the funnel.
 */
export const VM_ANALYTICS_FUNNELS = {
  /** The core question: does a creation request actually deliver assets? */
  coreAssetCreation: defineFunnel({
    label: "Core asset creation",
    description: "Requested → delivered, for every surface and operation. The base health metric.",
    steps: [EVMAnalyticsEvent.ASSET_CREATE_STARTED, EVMAnalyticsEvent.ASSET_CREATE_COMPLETED],
    breakdowns: ["surface", "output_type", "generation_scope", "transform_type"],
    conversionWindowDays: 1,
  }),

  /**
   * Headline "assets created" number. Scoped to de novo synthesis so upscales and
   * background removals do not inflate it — the cheap filter that lets edits and
   * generations share one event family. See EVMTransformType.
   */
  denovoAssetCreation: defineFunnel({
    label: "De novo asset creation",
    description: "Core creation restricted to GENERATE. Use for headline creation volume.",
    steps: [EVMAnalyticsEvent.ASSET_CREATE_STARTED, EVMAnalyticsEvent.ASSET_CREATE_COMPLETED],
    filters: { transform_type: EVMTransformType.GENERATE },
    breakdowns: ["surface", "output_type", "generation_scope"],
    conversionWindowDays: 1,
  }),

  /** Reliability view: how often a request ends in nothing, and why. */
  assetCreationFailure: defineFunnel({
    label: "Asset creation failure",
    description: "Requested → failed. Break down by failure_category on the terminal step.",
    steps: [EVMAnalyticsEvent.ASSET_CREATE_STARTED, EVMAnalyticsEvent.ASSET_CREATE_FAILED],
    breakdowns: ["surface", "output_type", "generation_scope", "transform_type"],
    conversionWindowDays: 1,
  }),

  /** Did a created asset make it all the way out of the product? */
  assetToDownload: defineFunnel({
    label: "Asset to download",
    description: "Creation through to a downloaded file — the strongest delivered-value chain.",
    steps: [
      EVMAnalyticsEvent.ASSET_CREATE_STARTED,
      EVMAnalyticsEvent.ASSET_CREATE_COMPLETED,
      EVMAnalyticsEvent.VIDEO_DOWNLOADED,
    ],
    breakdowns: ["surface"],
    // VIDEO_DOWNLOADED is client-fired from 7 call sites that do not know the surface
    // yet. Until they pass it, the surface breakdown drops the download step.
    blockedOn: ["surface"],
    conversionWindowDays: 7,
  }),

  /** Director Engine's full activation chain. */
  directorActivation: defineFunnel({
    label: "Director activation",
    description: "Director Engine: request → storyboard → assets → render → download.",
    steps: [
      EVMAnalyticsEvent.ASSET_CREATE_STARTED,
      EVMAnalyticsEvent.STORYBOARD_CREATED,
      EVMAnalyticsEvent.ASSET_CREATE_COMPLETED,
      EVMAnalyticsEvent.VIDEO_RENDER_SUCCEEDED,
      EVMAnalyticsEvent.VIDEO_DOWNLOADED,
    ],
    filters: { surface: EVMAssetSurface.DIRECTOR_ENGINE },
    // The storyboard, render and download steps declare `surface` but none of their fire
    // sites populate it yet, so this funnel reads as a cliff after step 1 until they do.
    // This is the single highest-value item in the fire-site migration.
    blockedOn: ["surface"],
    conversionWindowDays: 7,
  }),

  /**
   * Paid acquisition. No breakdowns: the steps share no property, because UTM tags live
   * on the anonymous view and plan details on checkout. Segment with PostHog person
   * properties instead of a step breakdown.
   */
  paidAcquisition: defineFunnel({
    label: "Paid acquisition",
    description: "Ad landing → signup → checkout → payment.",
    steps: [
      EVMAnalyticsEvent.LANDING_VIEWED,
      EVMAnalyticsEvent.SIGN_UP,
      EVMAnalyticsEvent.CHECKOUT_STARTED,
      EVMAnalyticsEvent.PURCHASE,
    ],
    conversionWindowDays: 14,
  }),

  /** Does hitting a credit wall actually convert to a top-up? */
  creditWallToPurchase: defineFunnel({
    label: "Credit wall to purchase",
    description: "Blocked on credits → checkout → payment.",
    steps: [
      EVMAnalyticsEvent.NOT_ENOUGH_CREDITS_UPSELL,
      EVMAnalyticsEvent.CHECKOUT_STARTED,
      EVMAnalyticsEvent.PURCHASE,
    ],
    conversionWindowDays: 7,
  }),

  /** Generation concurrency upsell (VM-880). Property names match across all three steps. */
  concurrencyUpsell: defineFunnel({
    label: "Concurrency upsell",
    description: "Queue hit → upgrade CTA shown → CTA clicked.",
    steps: [
      EVMAnalyticsEvent.GENERATION_QUEUE_HIT,
      EVMAnalyticsEvent.GENERATION_UPSELL_VIEW,
      EVMAnalyticsEvent.GENERATION_UPGRADE_CLICK,
    ],
    breakdowns: ["tier", "dimension", "queueReason"],
    conversionWindowDays: 1,
  }),
} satisfies Record<string, IVMFunnelDefinitionErased>

export type TVMFunnelKey = keyof typeof VM_ANALYTICS_FUNNELS

export const VM_ANALYTICS_FUNNEL_KEYS = Object.keys(VM_ANALYTICS_FUNNELS) as TVMFunnelKey[]

/** Runtime lookup returning the erased shape — for the audit, doc generator and CLI. */
export const getFunnelDefinition = (key: TVMFunnelKey): IVMFunnelDefinitionErased => VM_ANALYTICS_FUNNELS[key]
